import { Event, EventStatus, EventType } from './types';

/** Sample trips for demo mode. Dates are relative to `today` so the demo always looks upcoming. */
export interface DemoData {
  trips: string[];
  events: Event[];
  planningContent: Record<string, string>;
}

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

export function buildDemoData(today: Date = new Date()): DemoData {
  const day = (offset: number, hh = 12, mm = 0) =>
    new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset, hh, mm, 0, 0);
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const hm = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const stamp = (d: Date) => `${ymd(d)}T${hm(d)}`;

  const event = (
    trip: string,
    type: EventType,
    status: EventStatus,
    name: string,
    date: Date,
    endDate: Date | undefined,
    details: Record<string, string | number | boolean>,
    extra: Partial<Event> = {}
  ): Event => ({ id: newId(), trip, type, status, name, date, endDate, details, ...extra });

  const LISBON = 'Lisbon & Porto';
  const GREECE = 'Greek Islands';
  const TOKYO = 'Tokyo';

  const events: Event[] = [
    // Lisbon & Porto
    event(LISBON, 'flight', 'booked', 'Flight to Lisbon', day(7, 18, 40), day(8, 6, 55), {
      startDateTime: stamp(day(7, 18, 40)),
      endDateTime: stamp(day(8, 6, 55)),
      departureAirport: 'JFK',
      arrivalAirport: 'LIS',
      airline: 'TAP Air Portugal',
      flightNumber: 'TP 202',
      seat: '23A',
      confirmationNumber: 'K7XQ2M',
    }),
    event(LISBON, 'hotel', 'booked', 'Hotel in Alfama', day(8, 15), day(11, 11), {
      checkinDate: ymd(day(8)),
      checkoutDate: ymd(day(11)),
      propertyName: 'Memmo Alfama',
      address: 'Travessa das Merceeiras 27, Lisbon',
      totalCost: 780,
      paid: true,
      confirmationNumber: 'MA-55120',
    }, { location: 'Travessa das Merceeiras 27, Lisbon' }),
    event(LISBON, 'activity', 'planning', 'Fado show', day(8, 21, 30), undefined, {
      dateTime: stamp(day(8, 21, 30)),
      location: 'Clube de Fado, Alfama',
      ticketType: 'reserved',
      cost: 60,
    }, { location: 'Clube de Fado, Alfama' }),
    event(LISBON, 'activity', 'booked', 'Dinner at Taberna da Rua das Flores', day(9, 20), undefined, {
      dateTime: stamp(day(9, 20)),
      restaurantName: 'Taberna da Rua das Flores',
      partySize: 2,
      address: 'Rua das Flores 103, Lisbon',
      confirmationNumber: 'TRF-0412',
    }, { location: 'Rua das Flores 103, Lisbon' }),
    event(LISBON, 'transport', 'not-booked', 'Train to Sintra', day(10, 9, 10), day(10, 9, 50), {
      startDateTime: stamp(day(10, 9, 10)),
      endDateTime: stamp(day(10, 9, 50)),
      departureStation: 'Rossio',
      arrivalStation: 'Sintra',
      trainOperator: 'CP',
    }),
    event(LISBON, 'transport', 'booked', 'Train to Porto', day(11, 12, 39), day(11, 15, 28), {
      startDateTime: stamp(day(11, 12, 39)),
      endDateTime: stamp(day(11, 15, 28)),
      departureStation: 'Santa Apolónia',
      arrivalStation: 'Porto Campanhã',
      trainOperator: 'CP Alfa Pendular',
      trainNumber: '133',
      carNumber: '4',
      seat: '62',
      confirmationNumber: 'CP-88213',
    }),
    event(LISBON, 'hotel', 'booked', 'Hotel in Ribeira', day(11, 16), day(14, 11), {
      checkinDate: ymd(day(11)),
      checkoutDate: ymd(day(14)),
      propertyName: 'Pestana Vintage Porto',
      address: 'Praça da Ribeira 1, Porto',
      totalCost: 640,
      paid: false,
      confirmationNumber: 'PV-30981',
    }, { location: 'Praça da Ribeira 1, Porto' }),
    event(LISBON, 'activity', 'planning', 'Port cellar tour', day(12, 15), undefined, {
      dateTime: stamp(day(12, 15)),
      venue: 'Graham’s Port Lodge',
      address: 'Rua do Agro 141, Vila Nova de Gaia',
      guests: 2,
      cost: 50,
    }, { location: 'Vila Nova de Gaia' }),
    event(LISBON, 'flight', 'booked', 'Flight home', day(14, 11, 25), day(14, 14, 50), {
      startDateTime: stamp(day(14, 11, 25)),
      endDateTime: stamp(day(14, 14, 50)),
      departureAirport: 'OPO',
      arrivalAirport: 'JFK',
      airline: 'TAP Air Portugal',
      flightNumber: 'TP 1701',
      confirmationNumber: 'K7XQ2M',
    }),

    // Greek Islands
    event(GREECE, 'flight', 'booked', 'Flight to Athens', day(40, 17, 15), day(41, 10, 5), {
      startDateTime: stamp(day(40, 17, 15)),
      endDateTime: stamp(day(41, 10, 5)),
      departureAirport: 'JFK',
      arrivalAirport: 'ATH',
      airline: 'Delta',
      flightNumber: 'DL 200',
      seat: '31C',
      confirmationNumber: 'GX4P9L',
    }),
    event(GREECE, 'activity', 'booked', 'Acropolis guided tour', day(41, 16), undefined, {
      dateTime: stamp(day(41, 16)),
      location: 'Acropolis, Athens',
      ticketType: 'general',
      cost: 45,
      paid: true,
    }, { location: 'Acropolis, Athens' }),
    event(GREECE, 'transport', 'booked', 'Ferry to Naxos', day(42, 7, 25), day(42, 11, 10), {
      startDateTime: stamp(day(42, 7, 25)),
      endDateTime: stamp(day(42, 11, 10)),
      departurePort: 'Piraeus',
      arrivalPort: 'Naxos',
      ferryLine: 'Blue Star Ferries',
      vessel: 'Blue Star Delos',
      cabinSeat: 'Deck',
      confirmationNumber: 'BSF-7721',
    }),
    event(GREECE, 'hotel', 'planning', 'Villa in Naxos', day(42, 14), day(46, 10), {
      checkinDate: ymd(day(42)),
      checkoutDate: ymd(day(46)),
      propertyName: 'Agios Prokopios villa (Airbnb)',
      hostContact: 'Eleni',
      totalCost: 920,
    }, { location: 'Agios Prokopios, Naxos' }),
    event(GREECE, 'activity', 'not-booked', 'Boat day to Koufonisia', day(44, 9, 30), undefined, {
      dateTime: stamp(day(44, 9, 30)),
      venue: 'Naxos Boat Trips',
      guests: 2,
      cost: 180,
    }),
    event(GREECE, 'transport', 'planning', 'Ferry to Santorini', day(46, 12, 15), day(46, 14, 5), {
      startDateTime: stamp(day(46, 12, 15)),
      endDateTime: stamp(day(46, 14, 5)),
      departurePort: 'Naxos',
      arrivalPort: 'Santorini (Athinios)',
      ferryLine: 'SeaJets',
    }),
    event(GREECE, 'activity', 'booked', 'Sunset dinner in Oia', day(47, 19, 30), undefined, {
      dateTime: stamp(day(47, 19, 30)),
      restaurantName: 'Ammoudi Fish Tavern',
      partySize: 2,
      confirmationNumber: 'AMM-118',
    }, { location: 'Ammoudi Bay, Oia' }),
    event(GREECE, 'flight', 'booked', 'Flight home', day(49, 9, 50), day(49, 16, 20), {
      startDateTime: stamp(day(49, 9, 50)),
      endDateTime: stamp(day(49, 16, 20)),
      departureAirport: 'JTR',
      arrivalAirport: 'JFK',
      airline: 'Delta',
      flightNumber: 'DL 9411',
      confirmationNumber: 'GX4P9L',
    }),

    // Tokyo
    event(TOKYO, 'flight', 'booked', 'Flight to Tokyo', day(95, 12, 55), day(96, 15, 40), {
      startDateTime: stamp(day(95, 12, 55)),
      endDateTime: stamp(day(96, 15, 40)),
      departureAirport: 'JFK',
      arrivalAirport: 'HND',
      airline: 'ANA',
      flightNumber: 'NH 109',
      seat: '42K',
      checkedBags: 2,
      confirmationNumber: 'NH8Z3T',
    }),
    event(TOKYO, 'hotel', 'booked', 'Hotel in Shinjuku', day(96, 17), day(102, 11), {
      checkinDate: ymd(day(96)),
      checkoutDate: ymd(day(102)),
      propertyName: 'Hotel Gracery Shinjuku',
      address: '1-19-1 Kabukicho, Shinjuku',
      totalCost: 1150,
      paid: true,
      confirmationNumber: 'HG-662810',
    }, { location: 'Kabukicho, Shinjuku' }),
    event(TOKYO, 'activity', 'booked', 'teamLab Planets', day(97, 10), undefined, {
      dateTime: stamp(day(97, 10)),
      location: 'Toyosu, Tokyo',
      ticketType: 'reserved',
      cost: 32,
      paid: true,
    }, { location: 'Toyosu, Tokyo' }),
    event(TOKYO, 'activity', 'planning', 'Sushi omakase', day(97, 19), undefined, {
      dateTime: stamp(day(97, 19)),
      restaurantName: 'Sushi Saito',
      partySize: 2,
      specialRequests: 'Counter seats',
    }, { location: 'Minato, Tokyo' }),
    event(TOKYO, 'transport', 'not-booked', 'Train to Hakone', day(99, 8, 30), day(99, 10, 0), {
      startDateTime: stamp(day(99, 8, 30)),
      endDateTime: stamp(day(99, 10, 0)),
      departureStation: 'Shinjuku',
      arrivalStation: 'Hakone-Yumoto',
      trainOperator: 'Odakyu Romancecar',
    }),
    event(TOKYO, 'activity', 'planning', 'Ghibli Museum', day(100, 13), undefined, {
      dateTime: stamp(day(100, 13)),
      location: 'Mitaka, Tokyo',
      ticketType: 'reserved',
      cost: 7,
    }, { location: 'Mitaka, Tokyo' }),
    event(TOKYO, 'flight', 'booked', 'Flight home', day(102, 10, 25), day(102, 22, 10), {
      startDateTime: stamp(day(102, 10, 25)),
      endDateTime: stamp(day(102, 22, 10)),
      departureAirport: 'HND',
      arrivalAirport: 'JFK',
      airline: 'ANA',
      flightNumber: 'NH 110',
      confirmationNumber: 'NH8Z3T',
    }),
  ];

  const planningContent: Record<string, string> = {
    [LISBON]:
      '<h2>Lisbon &amp; Porto</h2><p>Pastéis de nata tour, then a slow week of tiles, trams and port.</p>' +
      '<h3>To do</h3><ul><li>Buy Viva Viagem card at the airport</li><li>Book Sintra train (Pena Palace opens at 9:30)</li><li>Reserve port cellar tour</li></ul>' +
      '<h3>Ideas</h3><ul><li>Tram 28 early morning before the crowds</li><li>Livraria Lello in Porto</li></ul>',
    [GREECE]:
      '<h2>Greek Islands</h2><p>Athens → Naxos → Santorini. Beach days, ferries and long dinners.</p>' +
      '<h3>Packing</h3><ul><li>Reef-safe sunscreen</li><li>Motion sickness tablets for ferries</li><li>Light layers for windy decks</li></ul>' +
      '<h3>Still to book</h3><ul><li>Ferry to Santorini</li><li>Koufonisia boat day</li></ul>',
    [TOKYO]:
      '<h2>Tokyo</h2><p>First time in Japan: food, neighborhoods and a night in the mountains.</p>' +
      '<h3>To do</h3><ul><li>Order pocket Wi-Fi</li><li>Ghibli tickets release on the 10th of the prior month</li><li>Load Suica on phone</li></ul>' +
      '<h3>Neighborhoods</h3><ul><li>Shimokitazawa for vintage</li><li>Yanaka for old Tokyo</li></ul>',
  };

  return { trips: [LISBON, GREECE, TOKYO], events, planningContent };
}
